import { useEffect, useMemo } from 'react';
import { useGetAtsJobDescriptionQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { convertHtmlToFormattedText } from 'modules/ats/utils/convertHtmlToFormattedText';
import { logger } from 'core/utils/logger';
import { getAtsEmbedContext } from '../utils/atsEmbedContext';
import { getEmbeddedJobId } from '../utils/embeddedJobId';

export type EmbeddedJobDescription =
  | { status: 'loading' }
  | { status: 'ready'; text: string | null };

// Bullhorn sends only the vacancy id, so the description comes from Kombo.
export const useEmbeddedJobDescription = ({
  enabled = true
}: { enabled?: boolean } = {}): EmbeddedJobDescription => {
  const organization = useOrganization();
  const jobId = enabled
    ? getEmbeddedJobId(getAtsEmbedContext(), organization.id)
    : null;
  const { data, loading, error } = useGetAtsJobDescriptionQuery({
    variables: {
      input: {
        organizationId: organization.id,
        pageSize: 1,
        filter: { atsJobIds: [jobId ?? ''] }
      }
    },
    skip: !jobId
  });

  useEffect(() => {
    if (!error) return;
    logger.error(error, {
      message: 'Failed to load the Bullhorn vacancy description',
      organizationId: organization.id,
      atsJobId: jobId
    });
  }, [error, organization.id, jobId]);

  const description = data?.listAtsJobs?.jobs.find(
    (job) => job.atsJobId === jobId
  )?.description;
  const text = useMemo(
    () => (description ? convertHtmlToFormattedText(description) : '') || null,
    [description]
  );

  if (!jobId) return { status: 'ready', text: null };
  if (loading) return { status: 'loading' };
  return { status: 'ready', text };
};
